import { inject, injectable } from 'inversify'

import { AuthBackendApi, AuthBackendApiImpl } from '@writefull/core'

import { SessionStore } from '@infrastructure/stores/session/session-store'

import { SignInTrackingManager } from '../tracking/sign-in-tracking-manager'

const DELTA_SESSION_EXPIRED = 600 // 10 minutes

@injectable()
export class SessionManager {
  constructor(
    @inject(AuthBackendApiImpl) private readonly authBackendApi: AuthBackendApi,
    @inject(SessionStore) private readonly sessionStore: SessionStore,
    @inject(SignInTrackingManager) private readonly signInTrackingManager: SignInTrackingManager,
  ) {}

  public async init(): Promise<void> {
    if (this.sessionStore.getState().isInitialized) {
      return
    }
    await this.tryLoadSession()
    this.sessionStore.getState().setIsInitialized()
  }

  public async signIn(email: string, password: string): Promise<void> {
    this.sessionStore.getState().start()

    try {
      const { data: session, error: sessionError } = await this.authBackendApi.signInWithEmail(
        email,
        password,
      )
      if (sessionError || !session?.idToken || !session.refreshToken) {
        throw sessionError ?? new Error('An error occurred on sign in.')
      }

      const { data: user, error: userError } = await this.authBackendApi.getUserDetails(
        session.idToken,
      )
      if (userError || !user?.uid) {
        throw userError ?? new Error('An error occurred on sign in.')
      }

      this.sessionStore.getState().finish(session, user)
      this.signInTrackingManager.track('sign-in')
    } catch {
      this.sessionStore.getState().fail()
    }
  }

  public signOut(): void {
    this.sessionStore.getState().clean()
  }

  public async getIdToken(): Promise<string | undefined> {
    const { idToken, expiresAt } = this.sessionStore.getState().session ?? {}
    if (!idToken || !expiresAt) {
      return undefined
    }

    const timeNow = Math.round(Date.now() / 1000)
    const _expiresAt = parseInt(expiresAt as unknown as string, 10)
    const expiresIn = _expiresAt - timeNow

    if (expiresIn <= DELTA_SESSION_EXPIRED) {
      await this.refreshToken()
    }

    const session = this.sessionStore.getState().session
    if (!session?.idToken) {
      return undefined
    }

    return session.idToken
  }

  private async tryLoadSession(): Promise<void> {
    await this.refreshToken()

    const session = this.sessionStore.getState().session
    if (!session?.idToken) {
      return
    }
    try {
      const { data: user, error: userError } = await this.authBackendApi.getUserDetails(
        session.idToken,
      )
      if (userError || !user?.uid) {
        return
      }

      this.sessionStore.getState().finish(session, user)
      this.signInTrackingManager.track('refresh')
    } catch {
      /* empty */
    }
  }

  private async refreshToken(): Promise<void> {
    try {
      const { refreshToken, idToken } = this.sessionStore.getState().session ?? {}
      if (!refreshToken || !idToken) {
        throw new Error('No current session.')
      }
      const { data, error } = await this.authBackendApi.refreshAccessToken(refreshToken)
      if (error || !data?.idToken || !data.refreshToken) {
        throw error ?? new Error('An error occurred on token refresh.')
      }

      this.sessionStore.getState().refresh(data.idToken, data.refreshToken, data.expiresAt)
    } catch {
      /*
       * TODO
       * if ((error as Error)?.message === 'TOKEN_EXPIRED') {
       *   this.removeSession()
       *   this.notifyAllSubscribers('REFRESH_TOKEN_EXPIRED')
       * }
       */
    }
  }
}
